import api from '../api';
import { toast } from 'sonner';
import { logger } from './logger';

/**
 * Link a newly created item to a commitment if a commitmentId was selected.
 * Call this after the item is created and you have its ID. Surfaces failures
 * (binder-operation observability — a swallowed link orphans the item).
 */
export async function linkToCommitment(commitmentId, itemId, itemType) {
  if (!commitmentId || !itemId) return;
  try {
    await api.post(`/commitments/${commitmentId}/link`, { itemId, itemType });
  } catch (err) {
    logger.error('Failed to link to commitment:', err);
    toast.error('Saved, but linking it to the commitment failed');
  }
}

/**
 * Reconcile an item's single-commitment link from `originalId` → `newId`
 * (edit-time F1). Unlinks the old, links the new. THROWS on failure so the
 * caller can surface a toast and skip the optimistic local-state update.
 */
export async function reconcileCommitmentLink(itemType, itemId, originalId, newId) {
  if (!itemId || newId === originalId) return;
  if (originalId) await api.delete(`/commitments/${originalId}/link/${itemType}/${itemId}`);
  if (newId) await api.post(`/commitments/${newId}/link`, { itemId, itemType });
}

/**
 * Decide + apply commitment inheritance when a task is dragged onto the
 * calendar (Calendar PATH-2). The new event inherits the task's PRIMARY
 * commitment (index 0). Returns the inherited commitmentId, or null when the
 * task is unbundled or the block has no id — in which case `linkFn` is NOT
 * called (avoids spurious empty links). `linkFn(commitmentId, eventId)` does
 * the actual link + its own error handling.
 */
export async function inheritCommitmentToEvent(task, block, linkFn) {
  const commitmentId = task?.commitmentIds?.[0] || null;
  if (!commitmentId || !block?.id) return null;
  await linkFn(commitmentId, block.id);
  return commitmentId;
}
